import React from 'react';
import { PersonDetail } from '../../../types/personDetail';
import { Phone, MapPin, Heart, User, BookOpenText, Church, Calendar, Briefcase } from 'lucide-react';
import { InfoCard, InfoRow } from '../../shared';

interface PersonAboutProps {
  personDetail: PersonDetail;
}

export const PersonAbout: React.FC<PersonAboutProps> = ({ personDetail }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Contact Information */}
      <InfoCard title="Contact Information" icon={<Phone size={20} />} editable onSave={(data) => console.log('Save contact', data)}>
        <InfoRow label="Email" value={personDetail.email} />
        <InfoRow label="Mobile Phone" value={personDetail.mobilePhone} />
        <InfoRow label="Home Phone" value={personDetail.homePhone} />
        <InfoRow label="Work Phone" value={personDetail.workPhone} />
        {personDetail.emergencyPhone && (
          <InfoRow label="Emergency Phone" value={personDetail.emergencyPhone} />
        )}
      </InfoCard>

      {/* Personal Information */}
      <InfoCard title="Personal Information" icon={<User size={20} />} editable onSave={(data) => console.log('Save personal', data)}>
        <InfoRow label="Gender" value={personDetail.gender} />
        <InfoRow label="Marital Status" value={personDetail.maritalStatus} />
        {personDetail.age && <InfoRow label="Age" value={`${personDetail.age} years`} />}
        <InfoRow label="Birthday" value={personDetail.birthday} />
        {personDetail.anniversary && <InfoRow label="Anniversary" value={personDetail.anniversary} />}
        {personDetail.familyPosition && (
          <InfoRow label="Family Position" value={personDetail.familyPosition} />
        )}
        {personDetail.householdName && (
          <InfoRow label="Household" value={personDetail.householdName} />
        )}
      </InfoCard>

      {/* Church Information */}
      <InfoCard title="Church Information" icon={<BookOpenText size={20} />} editable onSave={(data) => console.log('Save church', data)}>
        <InfoRow label="Campus" value={personDetail.campus || personDetail.primaryCampus} />
        <InfoRow label="Membership Type" value={personDetail.membershipType} />
        <InfoRow label="Membership Date" value={personDetail.membershipDate} />
        {personDetail.givingNumber && <InfoRow label="Giving Number" value={personDetail.givingNumber} />}
        {personDetail.active !== undefined && (
          <InfoRow label="Active" value={personDetail.active} type="boolean" />
        )}
      </InfoCard>

      {/* Addresses */}
      <InfoCard title="Addresses" icon={<MapPin size={20} />} editable onSave={(data) => console.log('Save addresses', data)}>
        {personDetail.mailingAddress && (
          <InfoRow label="Mailing Address" value={personDetail.mailingAddress} />
        )}
        {personDetail.homeAddress && personDetail.homeAddress !== personDetail.mailingAddress && (
          <InfoRow label="Home Address" value={personDetail.homeAddress} />
        )}
        {!personDetail.mailingAddress && !personDetail.homeAddress && personDetail.address && (
          <InfoRow label="Address" value={personDetail.address} />
        )}
      </InfoCard>

      {/* Faith Journey */}
      {(personDetail.baptized || personDetail.commitmentDate || personDetail.commitmentStory) && (
        <InfoCard title="Faith Journey" icon={<Church size={20} />} editable onSave={(data) => console.log('Save faith', data)}>
          <InfoRow label="Baptized" value={personDetail.baptized} type="boolean" />
          {personDetail.baptizedDate && <InfoRow label="Baptized Date" value={personDetail.baptizedDate} />}
          {personDetail.baptizedNote && <InfoRow label="Baptism Note" value={personDetail.baptizedNote} />}
          {personDetail.commitmentDate && <InfoRow label="Commitment Date" value={personDetail.commitmentDate} />}
          {personDetail.commitmentStory && (
            <div className="mt-2">
              <span className="text-sm font-medium text-gray-700">Commitment Story:</span>
              <p className="text-sm text-gray-600 mt-1">{personDetail.commitmentStory}</p>
            </div>
          )}
        </InfoCard>
      )}

      {/* Engagement */}
      {(personDetail.lastAttended || personDetail.lastGift) && (
        <InfoCard title="Engagement" icon={<Calendar size={20} />}>
          {personDetail.lastAttended && <InfoRow label="Last Attended" value={personDetail.lastAttended} />}
          {personDetail.lastGift && <InfoRow label="Last Gift" value={personDetail.lastGift} />}
          {personDetail.preferredService && (
            <InfoRow label="Preferred Service" value={personDetail.preferredService} />
          )}
        </InfoCard>
      )}

      {/* Medical & Safety */}
      <InfoCard title="Medical & Safety" icon={<Heart size={20} />} editable onSave={(data) => console.log('Save medical', data)}>
        {personDetail.allergies && <InfoRow label="Allergies" value={personDetail.allergies} />}
        {personDetail.confirmedNoAllergies !== undefined && (
          <InfoRow label="Confirmed No Allergies" value={personDetail.confirmedNoAllergies} type="boolean" />
        )}
        {personDetail.emergencyContactName && (
          <InfoRow label="Emergency Contact" value={personDetail.emergencyContactName} />
        )}
        {personDetail.emergencyPhone && (
          <InfoRow label="Emergency Phone" value={personDetail.emergencyPhone} />
        )}
      </InfoCard>

      {/* Work & Education */}
      {(personDetail.workTitle || personDetail.military || personDetail.schoolGrade) && (
        <InfoCard title="Work & Education" icon={<Briefcase size={20} />} editable onSave={(data) => console.log('Save work', data)}>
          {personDetail.workTitle && <InfoRow label="Job Title" value={personDetail.workTitle} />}
          {personDetail.military && <InfoRow label="Military" value={personDetail.military} />}
          {personDetail.schoolGrade && <InfoRow label="School Grade" value={personDetail.schoolGrade} />}
        </InfoCard>
      )}
    </div>
  );
};
